/**
 * RTWO Event Bus — Service Factory
 */

import { ebusInternalEventBus } from '../events/ebus-internal-event-bus';
import {
  engineSourceAdapterRegistry,
  type EngineSourceAdapterRegistry,
} from '../registry/adapter-registries';
import { createEBusRepositories, type EBusRepositories } from '../repositories/ebus-repositories';
import { EventRegistryService } from './event-registry-service';
import { TopicRegistryService } from './topic-registry-service';
import { PublisherRegistryService } from './publisher-registry-service';
import { SubscriberRegistryService } from './subscriber-registry-service';
import { RoutingEngineService } from './routing-engine-service';
import { DeliveryEngineService } from './delivery-engine-service';
import { RetryManagerService } from './retry-manager-service';
import { DeadLetterQueueService } from './dead-letter-queue-service';
import { MetadataService } from './metadata-service';
import { EventBusRegistryService } from './event-bus-registry-service';
import { EventPublisherService } from './event-publisher-service';
import { EBusReadinessService } from './ebus-readiness-service';
import type { EBusServiceContext } from './base-ebus-service';

export interface EBusEngineServices {
  eventRegistry: EventRegistryService;
  topicRegistry: TopicRegistryService;
  publisherRegistry: PublisherRegistryService;
  subscriberRegistry: SubscriberRegistryService;
  routingEngine: RoutingEngineService;
  deliveryEngine: DeliveryEngineService;
  retryManager: RetryManagerService;
  deadLetterQueue: DeadLetterQueueService;
  metadataService: MetadataService;
  busRegistry: EventBusRegistryService;
  eventPublisher: EventPublisherService;
  readinessService: EBusReadinessService;
}

export function createEBusEngineServices(options?: {
  repositories?: EBusRepositories;
  engineSourceRegistry?: EngineSourceAdapterRegistry;
  eventBus?: typeof ebusInternalEventBus;
}): {
  repositories: EBusRepositories;
  engineSourceRegistry: EngineSourceAdapterRegistry;
  services: EBusEngineServices;
  context: EBusServiceContext;
} {
  const repositories = options?.repositories ?? createEBusRepositories();
  const engineSourceRegistry = options?.engineSourceRegistry ?? engineSourceAdapterRegistry;
  const context: EBusServiceContext = {
    eventBus: options?.eventBus ?? ebusInternalEventBus,
  };

  const services: EBusEngineServices = {
    eventRegistry: new EventRegistryService(context, repositories, engineSourceRegistry),
    topicRegistry: new TopicRegistryService(context, repositories),
    publisherRegistry: new PublisherRegistryService(context, repositories, engineSourceRegistry),
    subscriberRegistry: new SubscriberRegistryService(context, repositories, engineSourceRegistry),
    routingEngine: new RoutingEngineService(context, repositories, engineSourceRegistry),
    deliveryEngine: new DeliveryEngineService(context, repositories, engineSourceRegistry),
    retryManager: new RetryManagerService(context, engineSourceRegistry),
    deadLetterQueue: new DeadLetterQueueService(context, repositories, engineSourceRegistry),
    metadataService: new MetadataService(context, repositories, engineSourceRegistry),
    busRegistry: new EventBusRegistryService(context),
    eventPublisher: new EventPublisherService(context, repositories, engineSourceRegistry),
    readinessService: new EBusReadinessService(context),
  };

  services.busRegistry.setServiceDescriptors([
    services.eventRegistry.getDescriptor(),
    services.topicRegistry.getDescriptor(),
    services.publisherRegistry.getDescriptor(),
    services.subscriberRegistry.getDescriptor(),
    services.routingEngine.getDescriptor(),
    services.deliveryEngine.getDescriptor(),
    services.retryManager.getDescriptor(),
    services.deadLetterQueue.getDescriptor(),
    services.metadataService.getDescriptor(),
    services.busRegistry.getDescriptor(),
    services.eventPublisher.getDescriptor(),
    services.readinessService.getDescriptor(),
  ]);

  return {
    repositories,
    engineSourceRegistry,
    services,
    context,
  };
}
